import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { shallowEqual, useSelector } from 'react-redux';
import { get, map } from 'lodash';
import { RootState } from 'modules';
import scrollIntoView from 'scroll-into-view';
import OrderingButton from 'components/base/OrderingButton';
import SearchInput from 'components/base/SearchInput';
import MentorCard from 'components/mentoring/mentor/MentorCard';
import MentorFilterModal from 'components/modal/MentorFilterModal';
import { getMentors, getInterestedMentors } from 'libs/api/user';
import { useDataApi, usePagination } from 'libs/hooks';
import { TListResponse, TPagination, TModalState } from 'modules/types';
import { TSimpleMentor } from 'modules/user';
import { CWindowWidth } from 'constants/base.const';
import { CMentoringFieldType } from 'constants/mentoring.const';
import { EUserType } from 'enums/base.enum';
import { Tabs, List, Tag, Col, Row, Typography, Button, Badge } from 'antd';
import styled from 'styled-components';
import { PrimaryColor, device } from 'GlobalStyles';
import BackgroundSource from 'assets/images/banners/11.webp';

const { TabPane } = Tabs;
const { Text, Title } = Typography;
const MentoringMentorListWrapper = styled.div`
  .title_wrapper {
    width: 100%;
    /* height: 380px; */
    height: 300px;
    background: linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), url(${BackgroundSource});
    background-size: cover;
    padding: 60px 10px;

    //title
    .ant-typography {
      width: 100%;
      max-width: ${CWindowWidth - 300}px;
      color: #ffffff;
      margin: auto;
      display: block;
    }
    //text
    span.ant-typography {
      font-size: 16px;
    }
    @media screen and ${device.md} {
      height: 250px;
      padding: 40px 10px;
      span.ant-typography {
        font-size: 12px;
      }
    }
  }
  .small-btn {
    padding: 0px 12px !important;
  }
  .ant-typography {
    color: #000;
  }
  margin-bottom: 20px;
  .filter-tag {
    background: transparent;
    border-radius: 8px;
    border: 1px solid ${PrimaryColor};
    padding: 3px 10px;
    color: ${PrimaryColor};
    font-size: 13px;
    .anticon-close {
      color: ${PrimaryColor};
    }
  }
`;

//전체 페이지
const MentoringMentorList = () => {
  const [key, setKey] = useState('all');
  const currentUser = useSelector((state: RootState) => state.user.current_user, shallowEqual);

  //헤더
  const ListHeader = ({
    pagination,
    reloadPage,
    total,
  }: {
    pagination: TPagination;
    reloadPage: any;
    total: number;
  }) => {
    const [mentorFilterModal, setMentorFilterModal] = useState<TModalState>({
      record: '',
      visible: false,
    });
    const [filterTagList, setFilterTagList] = useState<Array<string>>([]);
    const [tagCount, setTagCount] = useState(0);

    return (
      <>
        <Helmet>
          <title>
            {currentUser?.primary_field
              ? `${CMentoringFieldType[currentUser?.primary_field]} - 전체 멘토 보기`
              : '전체 멘토 보기'}
          </title>
        </Helmet>
        <Row id='card-list-header' justify='space-between' gutter={[16, 6]}>
          <Col xs={24} sm={8} md={8} lg={8} xl={8}>
            <Text strong>총 {total}명의 멘토</Text>
          </Col>
          <Col xs={12} sm={8} md={8} lg={8} xl={8} style={{ textAlign: 'center' }}>
            <SearchInput pagination={pagination} reloadPage={reloadPage} placeHolder='검색어 입력' />
          </Col>
          <Col xs={12} sm={8} md={8} lg={8} xl={8}>
            <div style={{ float: 'right' }}>
              <Badge count={tagCount} dot>
                <Button
                  size='small'
                  onClick={() => {
                    setMentorFilterModal({
                      visible: true,
                      onFinish: (data: any) => {
                        reloadPage({
                          current: 1,
                          pageSize: pagination.pageSize,
                          companyName: get(data, 'companyName'),
                          // jobType: get(data, 'jobType'),
                          countryType: get(data, 'countryType'),
                          positionType: get(data, 'positionType'),
                          available_times: get(data, 'available_times'),
                        });
                        setFilterTagList(get(data, 'tagNames', []));
                        setTagCount(get(data, 'count'));
                      },
                    });
                  }}
                >
                  필터
                </Button>
              </Badge>
              <OrderingButton
                pagination={pagination}
                reloadPage={reloadPage}
                defaultKey='-num_of_rater'
                orderingList={{
                  //'-score_average': '평점 높은순',
                  '-num_of_rater': '리뷰 많은순',
                  '-mentoring_count': '멘토링 많은순',
                }}
              />
            </div>
            <MentorFilterModal modalState={mentorFilterModal} setModalState={setMentorFilterModal} />
          </Col>
        </Row>
        <div>
          {map(filterTagList, (v) => (
            <Tag className='filter-tag' visible={true}>
              {v}
            </Tag>
          ))}
        </div>
      </>
    );
  };

  //리스트& 개별 카드
  const MentorListAll = () => {
    const [pagination, setPagination] = usePagination(16);
    const [isFirstRendering, setIsFirstRendering] = useState(true);
    const reloadPage = (page?: Partial<TPagination>) => {
      setPagination({
        ...pagination,
        ...page,
      });
      setIsFirstRendering(false);
    };
    const getPromise = getMentors.bind(null, {
      params: {
        current: pagination.current,
        pageSize: pagination.pageSize,
        q: pagination.q,
        ordering: pagination.ordering,
        companyName: pagination.companyName,
        // jobType: pagination.jobType, //전현직
        countryType: pagination.countryType,
        positionType: pagination.positionType,
        mentoringField: currentUser?.primary_field,
        available_times: pagination.available_times,
      },
    } as any);
    // console.log("pagination",pagination)

    const [{ data, loading }, setCallback] = useDataApi<TListResponse<TSimpleMentor>>(getPromise, {
      contents: [],
      last: false,
    });

    useEffect(() => {
      setCallback(() => getPromise);
    }, [pagination]);

    useEffect(() => {
      if (!loading && !isFirstRendering) {
        scrollIntoView(document?.getElementById('card-list-header') as HTMLElement, {
          align: {
            top: 0.3,
          },
        });
      }
    }, [loading]);

    return (
      <div
        style={{
          maxWidth: `${CWindowWidth - 200}px`,
          margin: '10px auto',
          padding: '0px 10px',
        }}
      >
        <ListHeader pagination={pagination} reloadPage={reloadPage} total={get(data, 'total')} />
        <List
          pagination={{
            ...pagination,
            total: get(data, 'total', 0),
            position: 'bottom',
            onChange: (page) => {
              reloadPage({
                current: page,
                pageSize: pagination.pageSize,
              });
            },
          }}
          loading={loading}
          style={{ paddingTop: '20px' }}
          grid={{ gutter: 16, xs: 1, sm: 2, md: 2, lg: 4, xl: 4, xxl: 4 }}
          dataSource={get(data, 'contents')}
          renderItem={(item) => (
            <List.Item>
              <MentorCard style={{ margin: 'auto' }} mentor={item} />
            </List.Item>
          )}
        />
      </div>
    );
  };

  const MentorListIntersted = () => {
    const [pagination, setPagination] = usePagination(16);
    const [isFirstRendering, setIsFirstRendering] = useState(true);
    const reloadPage = (page?: Partial<TPagination>) => {
      setPagination({
        ...pagination,
        ...page,
      });
      setIsFirstRendering(false);
    };
    const getPromise = getInterestedMentors.bind(null, {
      params: {
        current: pagination.current,
        pageSize: pagination.pageSize,
        q: pagination.q,
        ordering: pagination.ordering,
        companyName: pagination.companyName,
        jobType: pagination.jobType,
        countryType: pagination.countryType,
        positionType: pagination.positionType,
        mentoringField: currentUser?.primary_field,
      },
    });

    const [{ data, loading }, setCallback] = useDataApi<TListResponse<TSimpleMentor>>(getPromise, {
      contents: [],
      last: false,
    });

    useEffect(() => {
      setCallback(() => getPromise);
    }, [pagination]);

    useEffect(() => {
      if (!loading && !isFirstRendering) {
        scrollIntoView(document?.getElementById('card-list-header') as HTMLElement, {
          align: {
            top: 0.3,
          },
        });
      }
    }, [loading]);

    return (
      <div
        style={{
          maxWidth: `${CWindowWidth - 200}px`,
          margin: '10px auto',
          padding: '0px 10px',
        }}
      >
        <ListHeader pagination={pagination} reloadPage={reloadPage} total={get(data, 'total')} />
        <List
          pagination={{
            ...pagination,
            total: get(data, 'total', 0),
            position: 'bottom',
            hideOnSinglePage: true,
            onChange: (page) => {
              reloadPage({
                current: page,
                pageSize: pagination.pageSize,
              });
            },
          }}
          loading={{ spinning: loading, delay: 1000 }}
          style={{ paddingTop: '20px' }}
          grid={{ gutter: 16, xs: 1, sm: 2, md: 2, lg: 4, xl: 4, xxl: 4 }}
          dataSource={get(data, 'contents')}
          renderItem={(item) => (
            <List.Item>
              <MentorCard
                mentor={item}
                pagination={pagination}
                reloadPage={reloadPage}
                style={{ margin: 'auto' }}
              />
            </List.Item>
          )}
        />
      </div>
    );
  };

  return (
    <MentoringMentorListWrapper>
      <div className='title_wrapper'>
        <Title>Best-in-Class</Title>
        <Title>Mentor Group</Title>
        <Text style={{ marginTop: '10px' }}>
          Top-tier firm 출신의 엄선된 전·현직 멘토
          <br />
          검증된 Mock interview 경험에 기반한 정확하고 세심한 피드백
          <br />
          Up-to-date 업계 최신 정보 보유
        </Text>
      </div>
      {currentUser?.user_type === EUserType.Mentee ? (
        <Tabs
          activeKey={key}
          onChange={(e) => {
            setKey(e);
          }}
          centered={true}
        >
          <TabPane tab='전체 멘토' key='all'>
            <MentorListAll />
          </TabPane>
          <TabPane tab='찜한 멘토' key='interested'>
            <MentorListIntersted />
          </TabPane>
        </Tabs>
      ) : (
        <div style={{ marginTop: '40px' }}>
          <MentorListAll />
        </div>
      )}
    </MentoringMentorListWrapper>
  );
};

export default MentoringMentorList;
